import { AppPageHeader, Group } from '@/components/_common';
import { useFragaProducts } from '@/core/domain/operational/Fraga/Fraga.hooks';
import { FragaProduct } from '@/core/domain/operational/Fraga/Fraga.types';
import { Button, Divider, Image, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useState } from 'react';
import { FaImage } from 'react-icons/fa';
import { useDebounce } from '@/hooks';
import { CheckOutlined } from '@ant-design/icons';

interface Props {
  onCallback: (product: FragaProduct) => void;
  productSelectId?: string;
}

export default function PartsFragaList({ productSelectId, onCallback }: Props) {
  const [params, setParams] = useState<{
    take: number;
    skip: number;
    query?: string;
  }>({
    take: 10,
    skip: 0,
  });

  const currentPage = Math.round(params.skip / params.take) || 1;

  const query = useDebounce(params, 500);

  const { data: fragaData, isLoading } = useFragaProducts({ ...query });

  const pageInfo = fragaData?.data.catalogSearchByPlate.pageInfo;

  const fragaProducts = fragaData?.data.catalogSearchByPlate.nodes.map(
    (node) => node.product,
  );

  function handleSelectItem(row: FragaProduct) {
    onCallback(row);
  }

  const columns: ColumnsType<FragaProduct> = [
    {
      key: 'applicationDescription',
      dataIndex: 'applicationDescription',
      title: 'Descrição',
      render: (description, row) => (
        <Group gap={16}>
          <Image
            width={40}
            height={40}
            src={
              !!row?.images?.length ? row?.images[0]?.thumbnailUrl : undefined
            }
            wrapperStyle={{ flexShrink: 0 }}
            placeholder={
              <Group position="center" style={{ height: '100%' }}>
                <FaImage color="lightgray" />
              </Group>
            }
          />
          <span>{description}</span>
        </Group>
      ),
    },
    {
      title: 'Cód. Barras',
      render(_, row) {
        return (
          row?.specifications?.find(
            (item) => item.description === 'Código de barras (EAN)',
          )?.value || '-'
        );
      },
    },
    {
      title: 'NCM',
      render(_, row) {
        return (
          row?.specifications?.find((item) => item.description === 'NCM')
            ?.value || '-'
        );
      },
    },
    {
      title: 'Grupo',
      render(_, row) {
        return row?.productGroup?.family?.name || '-';
      },
    },
    {
      title: 'Fabricante',
      render(_, row) {
        return row?.brand?.name || '-';
      },
    },
    {
      dataIndex: 'actions',
      align: 'right',
      render: (_, row) => (
        <Button
          disabled={productSelectId === row.productId}
          type="primary"
          onClick={() => handleSelectItem(row)}
          icon={<CheckOutlined />}
        />
      ),
      width: 90,
    },
  ];

  return (
    <>
      <AppPageHeader
        searchValue={params.query}
        onSearch={(value) =>
          setParams((prev) => ({ ...prev, query: value, skip: 0 }))
        }
      />

      <Divider />

      <Image.PreviewGroup>
        <Table
          dataSource={fragaProducts}
          columns={columns}
          loading={isLoading}
          rowKey={(row) => row.productId}
          pagination={{
            pageSize: params.take,
            total: pageInfo?.total,
            current: currentPage,
            onChange(page, pageSize) {
              setParams((prev) => ({
                ...prev,
                take: pageSize,
                skip: prev.take !== pageSize ? 0 : page * pageSize,
              }));
            },
          }}
        />
      </Image.PreviewGroup>
    </>
  );
}
